/* eslint-disable camelcase */
import { TranslateService } from '@ngx-translate/core';
import { ITravel } from 'src/app/shared/interfaces/travels/ITravel';
import { WildcardSearchOperations } from 'src/app/shared/operations/wildcard-search.operations';
import { TRAVEL_STATUS } from '../../shared/enums/travel/TravelStatus.enum';
import { TRAVEl_TODOS_VIEWS } from '../../shared/enums/travel/TravelTodos.enum';
import { IPaginationProps } from '../../shared/interfaces/IPaginationProps';
import { IFilter } from '../../shared/interfaces/IFilter';
import { COMPARE_OPERATOR } from '../../shared/enums/CompareOperator.enum';
import { TableFilterOption } from '../../shared/components/table/table.component';

export class TravelTodosOperations {
  public static getStatusesToShow(currentView: TRAVEl_TODOS_VIEWS): TRAVEL_STATUS[] {
    switch (currentView) {
      case TRAVEl_TODOS_VIEWS.TEAM:
        return [TRAVEL_STATUS.PENDING_TEAM_MNG_APPROVAL];
      case TRAVEl_TODOS_VIEWS.DPT:
        return [TRAVEL_STATUS.PENDING_DEPARTMENT_MNG_APPROVAL];
      case TRAVEl_TODOS_VIEWS.BTAT:
        return [TRAVEL_STATUS.BTAT_PENDING_APPROVAL, TRAVEL_STATUS.BTAT_INSTRUCTIONS];
      default:
        return [TRAVEL_STATUS.COMPLETED, TRAVEL_STATUS.ACCOUNTED, TRAVEL_STATUS.EH_ACCOUNTING];
    }
  }

  public static getStatusesFilterOptions(currentView: string, translateService: TranslateService): TableFilterOption[] {
    switch (currentView) {
      case 'team_mng_view':
        return [{ label: translateService.instant('travels.status.2'), value: '2' }];
      case 'dpt_mng_view':
        return [{ label: translateService.instant('travels.status.12'), value: '12' }];
      case 'btat_mng_view':
        return [
          { label: translateService.instant('travels.status.8'), value: '8' },
          { label: translateService.instant('travels.status.6'), value: '6' },
        ];
      default:
        return [
          { label: translateService.instant('travels.status.17'), value: '17' },
          { label: translateService.instant('travels.status.19'), value: '19' },
          { label: translateService.instant('travels.status.20'), value: '20' },
        ];
    }
  }

  public static filterUniqueProjectNames(projects: ITravel['project'][]): ITravel['project'][] {
    const uniqueProjectNames: ITravel['project'][] = [];

    for (const project of projects) {
      const isUnique = !uniqueProjectNames.find(uniqueProjectName => uniqueProjectName.title === project.title);

      if (isUnique) {
        uniqueProjectNames.push(project);
      }
    }

    return uniqueProjectNames;
  }

  public static searchTravels(searchInput: string, travels: ITravel[]): ITravel[] {
    const propertiesToSearch = [
      'id',
      'employee.scdName',
      'employee.contractTypeName',
      'tripTitle',
      'status',
      'project.title',
      'tripStartDate',
      'tripEndDate',
    ];
    return travels.filter(travel => WildcardSearchOperations.isMatch(travel, propertiesToSearch, searchInput.toLowerCase()));
  }

  public static isReview(travelTodoItem: ITravel): boolean {
    return travelTodoItem.status === TRAVEL_STATUS.BTAT_PENDING_APPROVAL || travelTodoItem.status === TRAVEL_STATUS.ACCOUNTED;
  }

  public static getActionButtonText(travelTodoItem: ITravel, translateService: TranslateService): string {
    switch (travelTodoItem.status) {
      case TRAVEL_STATUS.BTAT_INSTRUCTIONS:
        return translateService.instant('travels.todos.actionButtons.insert-instructions') as string;
      case TRAVEL_STATUS.COMPLETED:
        return translateService.instant('travels.todos.actionButtons.add-concur') as string;
      default:
        return translateService.instant('travels.todos.actionButtons.review') as string;
    }
  }

  public static buildTravelsPaginationProps(
    fiscalYear: string,
    pageSize: number,
    filters: IFilter[],
    lastRead?: number,
    sort?: string,
    sortedLastReadValue?: string,
    searchValue?: string,
    comparisonOperator?: COMPARE_OPERATOR | '',
    searchFields?: string[],
    showCurrent?: boolean,
  ): IPaginationProps {
    // Input validation
    if (!fiscalYear) {
      throw new Error('fiscalYear is required');
    }

    if (!pageSize || pageSize <= 0) {
      throw new Error('pageSize must be a positive number');
    }

    if (!Array.isArray(filters)) {
      throw new Error('filters must be an array');
    }

    return {
      fiscalYear: fiscalYear.trim(),
      pageSize: Math.max(1, pageSize),
      filters,
      lastRead: lastRead ?? 0,
      sort: sort ?? null,
      sortedLastReadValue: sortedLastReadValue ?? null,
      searchValue: searchValue ?? null,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      comparisonOperator: comparisonOperator ?? null,
      searchFields: searchFields ?? [],
      // Using the implemented showAll field of accounts pagination
      showAll: showCurrent ?? false,
    };
  }
}
